import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  Badge,
  Card,
  CardBody,
  Spinner,
} from 'reactstrap';
import { motion } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import { formatCreatedDate, vendorTypeTranslations } from '../constants/utils';
import api from '../helpers/api';
import { AssetListTable } from './Common';
import AssetModal from './AssetModal';
import LocationModal from './LocationModal';

// Up-to-two-letter initials for a name (e.g. "Jane Doe" -> "JD"). Duplicated locally
// because the assign-picker's copy isn't exported.
const initialsOf = (name) => {
  const trimmed = (name || '').trim();
  if (!trimmed) return '?';
  return trimmed.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
};

const VendorModal = ({ isOpen, toggle, vendorData, loading, onEditVendor = null }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('info');
  const [vendorAssets, setVendorAssets] = useState([]);
  const [assetWarrantyData, setAssetWarrantyData] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [assetModal, setAssetModal] = useState({ isOpen: false, userData: null });
  const [locationModal, setLocationModal] = useState({ isOpen: false, data: null });
  const user = JSON.parse(localStorage.getItem('authUser'));
  const organizationId = user?.activeOrganizationId ?? null;

  const fetchVendorAssets = async () => {
    if (!vendorData?.id) return;

    setAssetsLoading(true);
    try {
      const { data } = await api.get(`asset/all?vendorId=${vendorData.id}&organizationId=${organizationId}`);
      // Extract assets from the nested structure
      const allAssets = data?.response?.[0]?.assets || [];
      setVendorAssets(allAssets);
    } catch (error) {
      console.error('Error fetching vendor assets:', error);
      setVendorAssets([]);
    } finally {
      setAssetsLoading(false);
    }
  };

  const fetchAssetWarrantyData = async () => {
    if (!vendorData?.vendorName) return;

    setContractsLoading(true);
    try {
      const { data } = await api.get(`asset-warranty-maintenance/get-all?vendorId=${vendorData?.id}&count=0`);
      setAssetWarrantyData(data?.response || []);
    } catch (error) {
      console.error('Error fetching asset warranty data:', error);
      setAssetWarrantyData([]);
    } finally {
      setContractsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'assets' && vendorData?.id) {
      fetchVendorAssets();
    }
    if (activeTab === 'contracts' && vendorData?.vendorName) {
      fetchAssetWarrantyData();
    }
  }, [activeTab, vendorData?.id, vendorData?.vendorName]);

  const getStatusBadgeColor = (status) => {
    const statusColors = {
      'Active': 'success',
      'Expired': 'danger',
      'Expiring Soon': 'warning',
      'Partially Covered': 'info'
    };
    return statusColors[status] || 'secondary';
  };

  const statusLabels = {
    'Active': t('shared.active', 'Active'),
    'Expired': t('shared.expired', 'Expired'),
    'Expiring Soon': t('shared.status_expiring_soon', 'Expiring Soon'),
    'Partially Covered': t('shared.status_partially_covered', 'Partially Covered')
  };

  if (!vendorData && !loading) return null;

  return (
    <>
    <Modal size="xl" isOpen={isOpen} toggle={toggle}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Enhanced Header */}
        <div
          className="modal-header border-0 bg-gradient-primary text-white position-sticky"
          style={{
            top: 0,
            zIndex: 10,
            // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
            padding: '1.5rem'
          }}
        >
          <div className="d-flex align-items-center">
            <div className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center me-3"
                 style={{ width: '40px', height: '40px' }}>
              <i className="bx bx-user text-white fs-5"></i>
            </div>
            <div>
              <h5 className="modal-title mb-0 fw-bold text-white">
                {vendorData?.vendorName || t('business.components.vendor_modal.title_vendor_details', 'Vendor Details')}
              </h5>
              <p className="text-white-50 small mb-0">{t('business.components.vendor_modal.subtitle_vendor_info', 'Vendor information and details')}</p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 ms-auto">
            {onEditVendor && vendorData && (
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                onClick={() => onEditVendor(vendorData)}
                title={t('shared.edit_vendor', 'Edit Vendor')}
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  color: '#495057',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  borderRadius: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.95)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <FeatherIcon icon="edit-3" size={16} />
                <span>{t('shared.edit_vendor', 'Edit Vendor')}</span>
              </button>
            )}
            <button
              onClick={toggle}
              type="button"
              className="btn-close btn-close-white"
              aria-label={t('shared.close', 'Close')}
            />
          </div>
        </div>

        {/* Navigation Tabs */}
        <Nav
          tabs
          className="border-bottom-0 px-4 pt-3 bg-light"
          style={{ borderRadius: '0' }}
        >
          <NavItem>
            <NavLink
              className={`nav-link-modern ${activeTab === 'info' ? 'active' : ''}`}
              onClick={() => setActiveTab('info')}
              style={{
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'info' ? '#fff' : 'transparent',
                color: activeTab === 'info' ? '#495057' : '#6c757d',
                fontWeight: activeTab === 'info' ? '600' : '400',
                cursor: 'pointer'
              }}
            >
              <i className="bx bx-info-circle me-2"></i>
              {t('shared.info', 'Info')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={`nav-link-modern ${activeTab === 'assets' ? 'active' : ''}`}
              onClick={() => setActiveTab('assets')}
              style={{
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'assets' ? '#fff' : 'transparent',
                color: activeTab === 'assets' ? '#495057' : '#6c757d',
                fontWeight: activeTab === 'assets' ? '600' : '400',
                cursor: 'pointer'
              }}
            >
              <i className="bx bx-package me-2"></i>
              {t('shared.assets', 'Assets')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={`nav-link-modern ${activeTab === 'contracts' ? 'active' : ''}`}
              onClick={() => setActiveTab('contracts')}
              style={{
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'contracts' ? '#fff' : 'transparent',
                color: activeTab === 'contracts' ? '#495057' : '#6c757d',
                fontWeight: activeTab === 'contracts' ? '600' : '400',
                cursor: 'pointer'
              }}
            >
              <i className="bx bx-file-text me-2"></i>
              {t('shared.contracts', 'Contracts')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={`nav-link-modern ${activeTab === 'spend' ? 'active' : ''}`}
              onClick={() => setActiveTab('spend')}
              style={{
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'spend' ? '#fff' : 'transparent',
                color: activeTab === 'spend' ? '#495057' : '#6c757d',
                fontWeight: activeTab === 'spend' ? '600' : '400',
                cursor: 'pointer'
              }}
            >
              <i className="bx bx-dollar me-2"></i>
              {t('shared.spend', 'Spend')}
            </NavLink>
          </NavItem>
        </Nav>

        {/* Modal Body */}
        <div 
          className="modal-body p-0" 
          style={{ 
            maxHeight: 'calc(90vh - 200px)',
            overflow: 'auto'
          }}
        >
          <TabContent activeTab={activeTab} className="h-100">
            {/* Info Tab */}
            <TabPane tabId="info">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div className="info-icon me-3">
                        <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                             style={{ width: '40px', height: '40px' }}>
                          <i className="bx bx-user text-primary fs-5"></i>
                        </div>
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold">{t('shared.vendor_information', 'Vendor Information')}</h5>
                        <p className="text-muted small mb-0">{t('business.components.vendor_modal.info_subtitle', 'Complete vendor details and contact information')}</p>
                      </div>
                    </div>

                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" size="lg" />
                        <p className="text-muted mt-3">{t('business.components.vendor_modal.loading_vendor_details', 'Loading vendor details...')}</p>
                      </div>
                    ) : (
                      vendorData && (
                        <Row className="g-4">
                          {/* Basic Information */}
                          <Col md={6}>
                            <div className="info-card bg-light rounded p-3 h-100">
                              <h6 className="text-primary mb-3 fw-semibold">
                                <i className="bx bx-info-circle me-2"></i>{t('shared.basic_information', 'Basic Information')}
                              </h6>
                              <div className="info-item mb-3">
                                <label className="text-muted small">{t('shared.vendor_name', 'Vendor Name')}</label>
                                <div className="fw-medium">{vendorData?.vendorName || t('shared.n_a', 'N/A')}</div>
                              </div>
                              <div className="info-item mb-3">
                                <label className="text-muted small">{t('shared.vendor_type', 'Vendor Type')}</label>
                                <div>
                                  <Badge color="primary" className="bg-opacity-10 text-primary border-0">
                                    {vendorTypeTranslations(t)[vendorData?.vendorType] || 
                                      (vendorData?.vendorType
                                        ?.replace(/_/g, " ")
                                        .toLowerCase()
                                        .replace(/\b\w/g, l => l.toUpperCase()) || t('shared.n_a', 'N/A'))}
                                  </Badge>
                                </div>
                              </div>
                              <div className="info-item mb-3">
                                <label className="text-muted small">{t('shared.vendor_role', 'Vendor Role')}</label>
                                <div>
                                  <Badge color="info" className="bg-opacity-10 text-info border-0">
                                    {vendorData?.vendorRole || t('shared.n_a', 'N/A')}
                                  </Badge>
                                </div>
                              </div>
                              <div className="info-item">
                                <label className="text-muted small">{t('shared.created_date', 'Created Date')}</label>
                                <div className="fw-medium">{formatCreatedDate(vendorData?.createdAt) || t('shared.n_a', 'N/A')}</div>
                              </div>
                            </div>
                          </Col>

                          {/* Contact Information */}
                          <Col md={6}>
                            <div className="info-card bg-light rounded p-3 h-100">
                              <h6 className="text-primary mb-3 fw-semibold">
                                <i className="bx bx-phone me-2"></i>{t('shared.contact_information', 'Contact Information')}
                              </h6>
                              {(() => {
                                const pc = vendorData?.contacts?.find((c) => c.primary) || vendorData?.contacts?.[0];
                                return pc ? (
                                  <div className="info-item mb-3">
                                    <label className="text-muted small">{t('business.vendors.contacts.primary_contact', 'Primary contact')}</label>
                                    <div className="fw-medium">{pc.fullName}{pc.role ? ` — ${pc.role}` : ''}</div>
                                  </div>
                                ) : null;
                              })()}
                              <div className="info-item mb-3">
                                <label className="text-muted small">{t('shared.email', 'Email')}</label>
                                <div className="fw-medium d-flex align-items-center">
                                  <FeatherIcon icon="mail" size={16} className="me-2 text-muted" />
                                  {vendorData?.email ? (
                                    <a href={`mailto:${vendorData.email}`} className="text-decoration-none">
                                      {vendorData.email}
                                    </a>
                                  ) : (
                                    <span>{t('shared.n_a', 'N/A')}</span>
                                  )}
                                </div>
                              </div>
                              <div className="info-item mb-3">
                                <label className="text-muted small">{t('shared.phone_number', 'Phone Number')}</label>
                                <div className="fw-medium d-flex align-items-center">
                                  <FeatherIcon icon="phone" size={16} className="me-2 text-muted" />
                                  {vendorData?.phoneNumber ? (
                                    <a href={`tel:${vendorData.phoneNumber}`} className="text-decoration-none">
                                      {vendorData.phoneNumber}
                                    </a>
                                  ) : (
                                    <span>{t('shared.n_a', 'N/A')}</span>
                                  )}
                                </div>
                              </div>
                              <div className="info-item">
                                <label className="text-muted small">{t('shared.address', 'Address')}</label>
                                <div className="fw-medium d-flex align-items-start">
                                  <FeatherIcon icon="map-pin" size={16} className="me-2 mt-1 text-muted flex-shrink-0" />
                                  <div>{vendorData?.address || t('shared.n_a', 'N/A')}</div>
                                </div>
                              </div>
                            </div>
                          </Col>

                          {/* Contacts */}
                          {vendorData?.contacts?.length > 0 && (
                            <Col md={12}>
                              <div className="info-card bg-light rounded p-3">
                                <h6 className="text-primary mb-3 fw-semibold">
                                  <i className="bx bx-group me-2"></i>{t('business.vendors.contacts.title', 'Contacts')}
                                </h6>
                                {[...vendorData.contacts]
                                  .sort((a, b) => (b.primary ? 1 : 0) - (a.primary ? 1 : 0))
                                  .map((contact, index) => (
                                    <div key={contact.id || index} className="bg-white rounded p-3 mb-2 d-flex align-items-center">
                                      <div className="me-3">
                                        <div
                                          className="bg-success bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center"
                                          style={{ width: '48px', height: '48px' }}
                                        >
                                          <span className="text-success fw-semibold">{initialsOf(contact.fullName)}</span>
                                        </div>
                                      </div>
                                      <div className="flex-grow-1">
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                          <h6 className="mb-0 fw-semibold">{contact.fullName}</h6>
                                          {contact.primary && (
                                            <span className="badge rounded-pill py-1 px-2 d-inline-flex align-items-center gap-1 bg-success bg-opacity-10 text-success">
                                              <FeatherIcon icon="star" size={12} />
                                              {t('business.vendors.contacts.primary', 'Primary')}
                                            </span>
                                          )}
                                        </div>
                                        {contact.role && (
                                          <Badge color="light" className="text-muted mt-1">{contact.role}</Badge>
                                        )}
                                        <div className="small mt-1 d-flex flex-wrap gap-3">
                                          {contact.email && (
                                            <a href={`mailto:${contact.email}`} className="text-decoration-none d-flex align-items-center">
                                              <FeatherIcon icon="mail" size={14} className="me-1 text-muted" />{contact.email}
                                            </a>
                                          )}
                                          {contact.phoneNumber && (
                                            <a
                                              href={`tel:+${contact.phoneCode || ''}${contact.phoneNumber}`}
                                              className="text-decoration-none d-flex align-items-center"
                                            >
                                              <FeatherIcon icon="phone" size={14} className="me-1 text-muted" />
                                              {contact.phoneCode ? `+${contact.phoneCode} ` : ''}{contact.phoneNumber}
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </Col>
                          )}

                          {/* Additional Information */}
                          {vendorData?.remarks && (
                            <Col md={12}>
                              <div className="info-card bg-light rounded p-3">
                                <h6 className="text-primary mb-3 fw-semibold">
                                  <i className="bx bx-file-text me-2"></i>{t('shared.additional_information', 'Additional Information')}
                                </h6>
                                <div className="info-item">
                                  <label className="text-muted small">{t('shared.remarks', 'Remarks')}</label>
                                  <div className="fw-medium">{vendorData.remarks}</div>
                                </div>
                              </div>
                            </Col>
                          )}
                        </Row>
                      )
                    )}
                  </CardBody>
                </Card>
              </div>
            </TabPane>

            {/* Assets Tab */}
            <TabPane tabId="assets">
              <div className="p-4">
                <AssetListTable
                  data={vendorAssets}
                  loading={assetsLoading}
                  emptyMessage={t('business.components.vendor_modal.no_assets_found', 'No assets found for this vendor')}
                  onAssetClick={(asset) => setAssetModal({ isOpen: true, userData: asset })}
                  onLocationClick={(asset) => setLocationModal({ isOpen: true, data: asset })}
                />
              </div>
            </TabPane>

            {/* Contracts Tab */}
            <TabPane tabId="contracts">
              <div className="p-4">
                {contractsLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" size="lg" />
                    <p className="text-muted mt-3">{t('shared.loading_contracts', 'Loading contracts...')}</p>
                  </div>
                ) : assetWarrantyData.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <i className="bx bx-file-text text-muted" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <p className="text-muted">{t('business.components.vendor_modal.no_contracts_found', 'No contracts found for this vendor')}</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>{t('shared.asset_id', 'Asset ID')}</th>
                          <th>{t('shared.manufacturer_warranty', 'Manufacturer Warranty')}</th>
                          <th>{t('shared.warranty_extension', 'Warranty Extension')}</th>
                          <th>{t('shared.maintenance_contract', 'Maintenance Contract')}</th>
                          <th>{t('shared.contract_details', 'Contract Details')}</th>
                          <th>{t('shared.overall_status', 'Overall Status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assetWarrantyData.map((asset, index) => (
                          <tr key={asset.id}>
                            <td>{index + 1}</td>
                            <td>
                              <div className="fw-medium">{asset.assetId}</div>
                            </td>
                            <td>
                              <div className="mb-1">
                                {asset.hasManufacturerWarranty ? (
                                  <>
                                    <Badge color={getStatusBadgeColor(asset.manufacturerWarrantyStatus)} className="mb-1">
                                      {statusLabels[asset.manufacturerWarrantyStatus] || asset.manufacturerWarrantyStatus}
                                    </Badge>
                                    <div className="small text-muted">
                                      <div>{new Date(asset.manufacturerWarrantyStartDate).toLocaleDateString()} - {new Date(asset.manufacturerWarrantyEndDate).toLocaleDateString()}</div>
                                      <div>{asset.manufacturerWarrantyDaysRemaining} days remaining</div>
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-muted small">{t('shared.no_warranty', 'No warranty')}</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="mb-1">
                                {asset.hasWarrantyExtension ? (
                                  <>
                                    <Badge color={getStatusBadgeColor(asset.warrantyExtensionStatus)} className="mb-1">
                                      {statusLabels[asset.warrantyExtensionStatus] || asset.warrantyExtensionStatus}
                                    </Badge>
                                    <div className="small text-muted">
                                      <div>{new Date(asset.warrantyExtensionStartDate).toLocaleDateString()} - {new Date(asset.warrantyExtensionEndDate).toLocaleDateString()}</div>
                                      <div>{asset.warrantyExtensionDaysRemaining} days remaining</div>
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-muted small">{t('shared.no_extension', 'No extension')}</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="mb-1">
                                <div className="fw-medium">{asset.maintenanceContractTypeDisplay}</div>
                                <div className="small text-muted">{t('shared.vendor', 'Vendor')}: {asset.maintenanceVendorName}</div>
                                <Badge color={getStatusBadgeColor(asset.contractStatus)}>
                                  {statusLabels[asset.contractStatus] || asset.contractStatus}
                                </Badge>
                              </div>
                            </td>
                            <td>
                              <div className="small">
                                <div className="mb-1"><strong>{t('shared.contract_number_short', 'Contract #')}:</strong> {asset.contractNumber}</div>
                                <div className="mb-1"><strong>{t('shared.period', 'Period')}:</strong> {new Date(asset.contractStartDate).toLocaleDateString()} - {new Date(asset.contractEndDate).toLocaleDateString()}</div>
                                <div className="mb-1"><strong>{t('shared.days_remaining', 'Days Remaining')}:</strong> {asset.contractDaysRemaining}</div>
                                <div className="mb-1"><strong>{t('shared.value', 'Value')}:</strong> {asset.contractValueFormatted}</div>
                                {asset.notes && (
                                  <div className="mt-2 p-2 bg-light rounded">
                                    <strong>{t('shared.notes', 'Notes')}:</strong> {asset.notes}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <Badge color={getStatusBadgeColor(asset.overallStatus)} className="mb-1">
                                {statusLabels[asset.overallStatus] || asset.overallStatus}
                              </Badge>
                              {asset.expiringSoon && (
                                <div className="small">
                                  <Badge color="warning" className="mt-1">
                                    <i className="bx bx-time-five me-1"></i>
                                    {t('shared.status_expiring_soon', 'Expiring Soon')}
                                  </Badge>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabPane>

             <TabPane tabId="spend">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div className="info-icon me-3">
                        <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                             style={{ width: '40px', height: '40px' }}>
                          <i className="bx bx-user text-primary fs-5"></i>
                        </div>
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold">{t('business.components.vendor_modal.spend_info_title', 'Total Spend Information')}</h5>
                        <p className="text-muted small mb-0">{t('business.components.vendor_modal.spend_info_subtitle', 'Complete vendor spend details')}</p>
                      </div>
                    </div>

                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" size="lg" />
                        <p className="text-muted mt-3">{t('shared.loading_details', 'Loading details...')}</p>
                      </div>
                    ) : (
                      vendorData && (
                        <Row className="g-4">
                          {/* Basic Information */}
                          <Col md={6}>
                            <div className="info-card bg-light rounded p-3 h-100">
                              <h6 className="text-primary mb-3 fw-semibold">
                                <i className="bx bx-info-circle me-2"></i>{t('shared.spend_information', 'Spend Information')}
                              </h6>
                              <div className="info-item mb-3">
                                <label className="text-muted small">{t('shared.asset_spend', 'Asset Spend')}</label>
                                <div className="fw-medium">{vendorData?.totalSpend?.totalAssetCost || t('shared.n_a', 'N/A')}</div>
                              </div>
                               <div className="info-item mb-3">
                                <label className="text-muted small">{t('shared.contract_spend', 'Contract Spend')}</label>
                                <div className="fw-medium">{vendorData?.totalSpend?.totalContractValue || t('shared.n_a', 'N/A')}</div>
                              </div>
                               <div className="info-item mb-3">
                                <label className="text-muted small">{t('shared.spare_parts_spend', 'Spare Parts Spend')}</label>
                                <div className="fw-medium">{vendorData?.totalSpend?.totalSparePartsValue || t('shared.n_a', 'N/A')}</div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      )
                    )}
                  </CardBody>
                </Card>
              </div>
            </TabPane>
          </TabContent>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer border-0 pt-0">
          <button type="button" className="btn btn-secondary" onClick={toggle}>
            {t('shared.close', 'Close')}
          </button>
        </div>
      </motion.div>
    </Modal>

    {assetModal.isOpen && (
      <AssetModal
        isOpen={assetModal.isOpen}
        toggle={() => setAssetModal({ isOpen: false, userData: null })}
        userData={assetModal.userData}
        loading={false}
        formatCreatedDate={formatCreatedDate}
      />
    )}

    {locationModal.isOpen && (
      <LocationModal
        isOpen={locationModal.isOpen}
        toggle={() => setLocationModal({ isOpen: false, data: null })}
        title={locationModal.data?.currentLocation}
        mainData={locationModal.data?.currentLocation}
        formatCreatedDate={formatCreatedDate}
      />
    )}
    </>
  );
};

export default VendorModal;